import { useMedplum } from '@medplum/react-hooks';
import { useEffect, useRef, useState } from 'react';
import type { Appointment } from '@medplum/fhirtypes';
import { fetchMedplumClientChartBundle } from '@/lib/medplumChartBundle';
import {
  loadEpisodeTreatmentPlanView,
} from '@/hooks/useChartEpisodeTreatmentPlan';
import { isMedplumHttpStatus } from '@/pages/appointments/utils/medplumClientErrors';
import type { TreatmentPlan } from '@/pages/clients/data/types';
import {
  resolveGroupPatientEpisode,
  shouldApplyTreatmentPlanResult,
} from '../lib/resolveGroupPatientEpisode';

export type TreatmentPlanLoadErrorKind =
  | 'missing-episode'
  | 'access'
  | 'load'
  | null;

export interface SessionPatientTreatmentPlanState {
  plans: TreatmentPlan[];
  loading: boolean;
  error: string | null;
  errorKind: TreatmentPlanLoadErrorKind;
}

type CacheEntry = SessionPatientTreatmentPlanState;

const EMPTY_STATE: SessionPatientTreatmentPlanState = {
  plans: [],
  loading: false,
  error: null,
  errorKind: null,
};

export function treatmentPlanErrorMessage(errorKind: TreatmentPlanLoadErrorKind): string | null {
  switch (errorKind) {
    case 'missing-episode':
      return 'No Group Therapy episode was found for this client.';
    case 'access':
      return 'You do not have access to this client’s treatment plan.';
    case 'load':
      return 'The treatment plan could not be loaded for this client.';
    default:
      return null;
  }
}

function isAccessError(err: unknown): boolean {
  if (isMedplumHttpStatus(err, 403)) return true;
  const message = err instanceof Error ? err.message.toLowerCase() : String(err).toLowerCase();
  return (
    message.includes('forbidden') ||
    message.includes('access denied') ||
    message.includes('not authorized') ||
    message.includes('403')
  );
}

export function useSessionPatientTreatmentPlan(options: {
  appointment: Appointment | null | undefined;
  patientId: string | undefined;
  groupId: string | undefined;
  enabled?: boolean;
}): SessionPatientTreatmentPlanState {
  const { appointment, patientId, groupId, enabled = true } = options;
  const medplum = useMedplum();
  const cacheRef = useRef<Record<string, CacheEntry>>({});
  const [stateByPatient, setStateByPatient] = useState<Record<string, CacheEntry>>({});
  const selectedIdRef = useRef(patientId);
  selectedIdRef.current = patientId;

  useEffect(() => {
    if (!enabled || !appointment?.id || !patientId) {
      return;
    }

    const cached = cacheRef.current[patientId];
    if (cached && !cached.loading) {
      setStateByPatient((prev) => ({ ...prev, [patientId]: cached }));
      return;
    }

    let cancelled = false;
    const requestedPatientId = patientId;

    const loadingState: CacheEntry = {
      plans: [],
      loading: true,
      error: null,
      errorKind: null,
    };
    cacheRef.current[requestedPatientId] = loadingState;
    setStateByPatient((prev) => ({ ...prev, [requestedPatientId]: loadingState }));

    void (async () => {
      try {
        const resolved = await resolveGroupPatientEpisode(medplum, {
          appointment,
          patientId: requestedPatientId,
          groupId,
        });

        if (!shouldApplyTreatmentPlanResult(requestedPatientId, selectedIdRef.current) || cancelled) {
          return;
        }

        if (!resolved.episodeId) {
          const next: CacheEntry = {
            plans: [],
            loading: false,
            error: treatmentPlanErrorMessage('missing-episode'),
            errorKind: 'missing-episode',
          };
          cacheRef.current[requestedPatientId] = next;
          setStateByPatient((prev) => ({ ...prev, [requestedPatientId]: next }));
          return;
        }

        const bundle = await fetchMedplumClientChartBundle(medplum, requestedPatientId);
        if (!shouldApplyTreatmentPlanResult(requestedPatientId, selectedIdRef.current) || cancelled) {
          return;
        }

        const episodes = bundle.episodes.filter((e) => e.id === resolved.episodeId);
        const clinicalNotes = bundle.clinicalNotes.filter(
          (n) => !n.episodeId || n.episodeId === resolved.episodeId,
        );

        const plans: TreatmentPlan[] = [];
        for (const episode of episodes) {
          try {
            const plan = await loadEpisodeTreatmentPlanView(
              medplum,
              requestedPatientId,
              episode,
              clinicalNotes,
            );
            if (plan) plans.push(plan);
          } catch {
            // Unreadable CarePlan; leave the list empty rather than showing a placeholder.
          }
        }

        if (!shouldApplyTreatmentPlanResult(requestedPatientId, selectedIdRef.current) || cancelled) {
          return;
        }

        const next: CacheEntry = {
          plans,
          loading: false,
          error: null,
          errorKind: null,
        };
        cacheRef.current[requestedPatientId] = next;
        setStateByPatient((prev) => ({ ...prev, [requestedPatientId]: next }));
      } catch (err) {
        if (!shouldApplyTreatmentPlanResult(requestedPatientId, selectedIdRef.current) || cancelled) {
          return;
        }
        const errorKind: TreatmentPlanLoadErrorKind = isAccessError(err) ? 'access' : 'load';
        const next: CacheEntry = {
          plans: [],
          loading: false,
          error: treatmentPlanErrorMessage(errorKind),
          errorKind,
        };
        cacheRef.current[requestedPatientId] = next;
        setStateByPatient((prev) => ({ ...prev, [requestedPatientId]: next }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, appointment, patientId, groupId, medplum]);

  if (!enabled || !patientId) {
    return EMPTY_STATE;
  }

  const current = stateByPatient[patientId];
  if (!current) {
    return { plans: [], loading: true, error: null, errorKind: null };
  }
  return current;
}
